import { buttonClass } from '@snitch/ui/button'
import { DAY_FORMS, DEVICE_FORMS, formatRub, plural } from '@snitch/ui/lib/format'
import { Brand } from '@snitch/ui/logo'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, Check } from 'lucide-react'
import { orpc } from '../../../lib/api.ts'
import { formatDay } from '../../../lib/billing.ts'

const orderQuery = (id: string) => orpc.billing.order.queryOptions({ input: { id } })

export const Route = createFileRoute('/_authed/_shell/orders/$orderId')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(orderQuery(params.orderId)),
  component: OrderPaid,
})

/** "Signal / Оплата · Успех". */
function OrderPaid() {
  const { orderId } = Route.useParams()
  const { data: order } = useSuspenseQuery(orderQuery(orderId))
  const q = order.quote
  const until = order.period ? formatDay(order.period.endsAt) : null

  const sub =
    order.kind === 'trial' && until
      ? `Тестовый тариф активен до ${until}. Подключение устройств появится совсем скоро — места уже за вами.`
      : order.kind === 'plan' && until
        ? `Тариф оплачен до ${until}. Устройства и ссылка подключения сохраняются при каждом продлении.`
        : 'Покупка прошла — всё уже в аккаунте.'

  return (
    <main className="flex min-h-dvh flex-col justify-between gap-10 bg-bg px-5 pt-[max(24px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-[max(32px,env(safe-area-inset-bottom))] text-ink md:mx-auto md:my-10 md:min-h-0 md:max-w-[480px] md:rounded-[36px] md:px-8 md:py-8">
      <Brand />
      <div className="flex flex-col gap-3.5">
        <span className="motion-success flex size-[72px] items-center justify-center rounded-full bg-kb-green-soft">
          <Check className="size-[34px] text-green-ink" aria-hidden="true" />
        </span>
        <h1 className="font-bold font-display text-5xl tracking-[-2px]">
          {order.status === 'paid' ? 'Оплачено' : 'Ждём оплату'}
        </h1>
        <p className="text-base leading-[1.45]">{sub}</p>
        <div className="flex flex-col gap-2.5 rounded-[20px] border border-line bg-card p-4 text-sm">
          {q.lines.map((line) => (
            <div key={line.kind} className="flex justify-between">
              <span>
                {line.kind === 'plan'
                  ? `${line.devices} ${plural(line.devices, DEVICE_FORMS)} · ${line.months} мес`
                  : line.kind === 'trial'
                    ? `Тест · ${line.devices} ${plural(line.devices, DEVICE_FORMS)} · ${line.days} ${plural(line.days, DAY_FORMS)}`
                    : line.kind === 'lte'
                      ? `LTE · ${line.gb} ГБ`
                      : 'Дополнительные места'}
              </span>
              <span className="font-semibold">{formatRub(line.price)}</span>
            </div>
          ))}
          {q.discounts.map((d) => (
            <div key={d.source} className="flex items-start justify-between gap-3">
              <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                {d.source === 'promo'
                  ? `Промокод ${d.ref ?? ''} · −${d.percent}%`
                  : d.source === 'referral_welcome'
                    ? `Скидка друга · −${d.percent}%`
                    : 'Скидка'}
              </span>
              <span className="shrink-0 font-semibold">−{formatRub(d.amount)}</span>
            </div>
          ))}
          {q.bonusSpent > 0 ? (
            <div className="flex justify-between">
              <span>Бонусами</span>
              <span className="font-semibold">−{formatRub(q.bonusSpent)}</span>
            </div>
          ) : null}
          <div className="flex justify-between border-line border-t pt-2.5 font-bold">
            <span>Итого</span>
            <span>{formatRub(q.total)}</span>
          </div>
        </div>
      </div>
      <Link to="/" className={buttonClass({ variant: 'ink', block: true })}>
        На главную
        <ArrowRight aria-hidden="true" />
      </Link>
    </main>
  )
}
